/* global require */

import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import Progress from "./generic/Progress";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import NavigationHandler from "./application/NavigationHandler";
import NotificationContainer from "../common/notification/NotificationContainer";
import LoadingContainer from "../common/loading/LoadingContainer";
import { DocPropKeys, retrieveDocumentProp } from "../utils/helpers/CustomXmlHelper";
import { useAppDispatch, useAppSelector } from "../redux/hooks/hooks";
import {
  selectAuthData,
  selectBackendUrl,
  selectClientId,
  selectCpmUser,
  selectIsAuthenticated,
  selectTenantId,
  selectUserFunctions,
  setCpmUser,
  setIxbrlSettings,
  setSeahorseDocumentLinked,
  setServiceProvider,
} from "../redux/feature/settings/configurations-slice";
import { openEventChannelAsync, selectElaborationStatusList } from "../redux/feature/object/remote-elaborations-slice";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../utils/constants/eventChannel";
import { AppDispatch, store } from "../redux/store/store";
import { useWindowSize } from "../utils/helpers/useWindowSizeHook";
import { ViewportTooNarrowError } from "./generic/ViewportTooNarrowError";
import { fetchIxbrlSettings, fetchSeahorseDocumentLinked, fetchServiceProvider } from "../pages/seahorse/SeahorseUtils";
import { isNotAuthorizedUser } from "../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../api/models/user-function-enum";
import { fetchCpmUser } from "../api/document/CpmUser";
import StandaloneModalContainer from "../common/standaloneModal/StandaloneModalContainer";

const MIN_SUPPORTED_WIDTH = 260;
const MIN_SUPPORTED_HEIGHT = 300;

const classNames = mergeStyleSets({
  pageContainer: {
    position: "relative",
    minHeight: "100vh",
  },

  contentWrap: {
    maxHeight: "90vh",
    paddingBottom: "2.5rem",
    overflow: "auto",
  },

  content: {
    height: "100%",
  },
  footer: {
    position: "absolute",
    bottom: "0",
    width: "100%",
    height: "2.5rem",
  },
});

export interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
}

const App: React.FunctionComponent<AppProps> = (props) => {
  const { title, isOfficeInitialized } = props;

  const dispatch: AppDispatch = useAppDispatch();

  const authData = useAppSelector(selectAuthData);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectCpmUser);
  const tenantId = useAppSelector(selectTenantId);
  const userFunctions = useAppSelector(selectUserFunctions);
  const clientId = useAppSelector(selectClientId);
  const backendUrl = useAppSelector(selectBackendUrl);
  const elaborations = useAppSelector(selectElaborationStatusList);
  const [channelId, setChannelId] = useState(undefined);

  useEffect(() => {
    if (tenantId && isAuthenticated) {
      fetchCpmUser().then((result) => {
        result.tenantId = tenantId;
        store.dispatch(setCpmUser(result));
        // eslint-disable-next-line no-undef
        console.log("fetchCpmUser resolved");
      });
    }
  }, [isAuthenticated, tenantId]);

  useEffect(() => {
    let notAuthorizedUserForIXBRL =
      isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions) &&
      isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);

    if (!notAuthorizedUserForIXBRL && isAuthenticated) {
      fetchServiceProvider().then((result) => store.dispatch(setServiceProvider(result)));
      fetchSeahorseDocumentLinked().then((result) => store.dispatch(setSeahorseDocumentLinked(result)));
      fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
    }
  }, [user, isAuthenticated, userFunctions]);

  useEffect(() => {
    // retrieves documentId from documentProps and sets channelId to documentId.
    // If no docId is present EVENT_CHANNEL_DEFAULT_TOPIC is used
    if (isOfficeInitialized)
      retrieveDocumentProp(DocPropKeys.DocumentId).then((documentId) => {
        setChannelId(documentId ?? EVENT_CHANNEL_DEFAULT_TOPIC);
      });
  }, [isOfficeInitialized]);

  useEffect(() => {
    if (channelId && user && clientId && authData && !elaborations) {
      // eslint-disable-next-line no-undef
      console.log("requesting sse channel open ...");
      dispatch(openEventChannelAsync({ documentId: channelId, userId: user.userName, baseUrl: backendUrl, clientId }));
    }
  }, [user, clientId, channelId, authData, isAuthenticated, elaborations]);

  const { width, height } = useWindowSize();

  const isViewPortSmall = useMemo(
    () => width <= MIN_SUPPORTED_WIDTH || height <= MIN_SUPPORTED_HEIGHT,
    [width, height]
  );

  if (!isOfficeInitialized) {
    return (
      <Progress
        title={title}
        logo={require("../../../../assets/logo-filled.png")}
        message="Please sideload your addin to see app body."
      />
    );
  } else {
    return (
      <>
        {isViewPortSmall && <ViewportTooNarrowError />}
        <LoadingContainer />
        <div className={classNames.pageContainer}>
          <div className={classNames.contentWrap}>
            <div className={classNames.content}>
              <NavigationHandler />
            </div>
          </div>
        </div>
        <NotificationContainer />
        <StandaloneModalContainer />
      </>
    );
  }
};

export default App;
